/**
 * datosDeLaCita.js — v2
 *
 * Lo que se sabe de una reunión **sin escucharla**: fecha, horario, quién la
 * convocó, dónde fue y a quién se invitó.
 *
 * Existe porque el acta salía con la cabecera en blanco —Fecha, Hora Inicio,
 * Hora Fin, Lugar, Convocado por— y parecía un fallo del modelo. No lo era. En
 * una transcripción de Teams real nadie dice en voz alta «hoy es 14 de
 * septiembre» ni «esta reunión la convoco yo»: el dato sencillamente no está en
 * lo que se le mandaba. El modelo devolvía vacío, que es exactamente lo que se
 * le pidió que hiciera antes que inventar.
 *
 * Pero la app sí lo sabe: son datos duros del calendario de Outlook, y ya se
 * enseñan en el panel «Información». Mandarlos cuesta unas decenas de tokens y
 * convierte media cabecera del acta en un dato cierto en vez de una conjetura.
 *
 * Qué manda sobre qué, y esto importa:
 *   - Fecha, horario, organizador, lugar e invitados → **el calendario**. Es
 *     autoridad; el modelo no debe discutirlo ni deducirlo de la conversación.
 *   - Todo lo demás —agenda, discusión, acuerdos, pendientes— → **la
 *     transcripción**. El calendario no sabe qué se dijo.
 */

const pad = (n) => String(n).padStart(2, '0');

/** "14 de septiembre de 2026", en español y sin depender de la zona horaria. */
function fechaLarga(fecha) {
  try {
    return fecha.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}`;
  }
}

/** "lunes" */
function diaDeLaSemana(fecha) {
  try {
    return fecha.toLocaleDateString('es', { weekday: 'long' });
  } catch {
    return null;
  }
}

/** "10:32" en la hora local de quien mira, que es la que espera ver en el acta. */
function hora(fecha) {
  return `${pad(fecha.getHours())}:${pad(fecha.getMinutes())}`;
}

const valida = (f) => f instanceof Date && !isNaN(f.getTime());

/**
 * Reúne los datos ciertos de una reunión para mandárselos al modelo.
 *
 * @param {Object|null} reunion - la cita del calendario
 * @param {Object|null} grabacion - la grabación, si se encontró
 * @returns {Object|null} null si no se sabe nada que merezca mandarse
 */
export function datosDeLaCita(reunion, grabacion = null) {
  const cita = {};

  if (reunion) {
    if (reunion.titulo) cita.titulo = reunion.titulo;
    if (reunion.organizador) cita.organizador = reunion.organizador;

    // Una reunión en Teams no tiene "sala". Decir "Microsoft Teams" es más útil
    // en un acta que dejar el lugar vacío.
    const lugar = reunion.ubicacion || (reunion.esEnLinea ? 'Microsoft Teams' : null);
    if (lugar) cita.lugar = lugar;

    const inicio = reunion.inicio instanceof Date ? reunion.inicio : (reunion.inicio ? new Date(reunion.inicio) : null);
    const fin = reunion.fin instanceof Date ? reunion.fin : (reunion.fin ? new Date(reunion.fin) : null);

    if (valida(inicio)) {
      cita.fecha = fechaLarga(inicio);
      cita.diaSemana = diaDeLaSemana(inicio);
      // Día, mes y año por separado y NO en formato ISO. Mandando "2026-09-14"
      // el modelo copiaba ese orden y numeraba la minuta como "260914", cuando el
      // acta del usuario numera en día-mes-año ("250826" = 25/ago/2026).
      cita.dia = pad(inicio.getDate());
      cita.mes = pad(inicio.getMonth() + 1);
      cita.anio = String(inicio.getFullYear());
      try { cita.mesNombre = inicio.toLocaleDateString('es', { month: 'long' }); } catch { /* sin locale, basta el número */ }
      cita.horaProgramada = hora(inicio);
    }
    if (valida(fin)) cita.finProgramado = hora(fin);
    if (valida(inicio) && valida(fin)) {
      cita.duracionMinutos = Math.round((fin - inicio) / 60000);
    }

    if (Array.isArray(reunion.asistentes) && reunion.asistentes.length > 0) {
      cita.invitados = reunion.asistentes;
    }
  }

  // La hora a la que empezó de verdad sale de la grabación, no del calendario:
  // una reunión convocada a las 10:30 puede arrancar a las 10:32. Se mandan las
  // dos porque el acta suele distinguir "Hora Programada" de "Hora Inicio".
  if (grabacion) {
    const real = grabacion.inicio instanceof Date ? grabacion.inicio : (grabacion.inicio ? new Date(grabacion.inicio) : null);
    if (valida(real)) cita.inicioReal = hora(real);
    if (grabacion.duracionSegundos) {
      cita.duracionGrabacionMinutos = Math.round(grabacion.duracionSegundos / 60);
    }
  }

  return Object.keys(cita).length > 0 ? cita : null;
}

export default datosDeLaCita;
